/**
 * React Router Configuration for Runtime UI
 *
 * Public-facing routes for product comparison journeys.
 * Routes are resolved by vertical key:
 * - /:vertical - Journey input form
 * - /:vertical/results - Comparison results
 * - /:vertical/product/:productId - Product details
 */

import type { ReactElement } from "react";
import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { AppLayout } from "./components/layout/AppLayout";

// Lazy load page components for code splitting
const HomePage = lazy(() => import("./pages/HomePage"));
const JourneyInputPage = lazy(() => import("./pages/JourneyInputPage"));
const ResultsPage = lazy(() => import("./pages/ResultsPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

/**
 * Suspense wrapper for lazy-loaded pages with loading spinner.
 */
function PageLoader({ children }: { readonly children: ReactElement }): ReactElement {
  return (
    <Suspense
      fallback={
        <div className="flex h-full min-h-[50vh] items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * Router configuration for the runtime UI.
 */
const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <PageLoader>
            <HomePage />
          </PageLoader>
        ),
      },
      {
        path: ":vertical",
        children: [
          {
            index: true,
            element: (
              <PageLoader>
                <JourneyInputPage />
              </PageLoader>
            ),
          },
          {
            path: "results",
            element: (
              <PageLoader>
                <ResultsPage />
              </PageLoader>
            ),
          },
          {
            path: "product/:productId",
            element: (
              <PageLoader>
                <ProductDetailPage />
              </PageLoader>
            ),
          },
        ],
      },
      {
        path: "*",
        element: (
          <PageLoader>
            <NotFoundPage />
          </PageLoader>
        ),
      },
    ],
  },
]);

/**
 * Router provider component for the runtime application.
 */
export function Router(): ReactElement {
  return <RouterProvider router={router} />;
}
